import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RefreshCw, X, CheckCircle, XCircle, Info } from "lucide-react";
import { adminService } from "@b2b/services/api/modules/admin/admin.service";
import { sellerService } from "@b2b/services/api/modules/seller/seller.service";
import {
  getProductDetailPath,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Button,
  StoreProductDialog,
  AdminproductTableConfig,
  TanStackDataTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from "@b2b/ui-components";
import { useAuth } from "../../auth/Authprovider";
import {
  useProductList,
  type UpdatedStatusFilter,
} from "../../hooks/useProductList";
import { useProductActions } from "../../hooks/useProductActions";
import { AdminProduct } from "../../types/product.types";
import { createProductColumns } from "./ProductList.columns";
import {
  buildPendingReviewDiffRows,
  buildSellerUpdateHistoryDiffRows,
  buildSubmittedSnapshotDisplayRows,
} from "./pendingReviewCompare";
import { RejectProductReasonDialog } from "./RejectProductReasonDialog";
import {
  getActiveFeaturedProductIds,
  parseFeaturedProductsFromSettings,
  toggleFeaturedProduct,
  toStorefrontFeaturedPayload,
  type FeaturedProductEntry,
} from "../../utils/featuredProducts";

interface ProductListProps {
  role?: "admin" | "seller";
}

type SellerUpdateHistoryRow = {
  id: string;
  status: string;
  submittedAt: string;
  resolvedAt: string | null;
  rejectionReason: string | null;
  submittedSnapshot?: Record<string, unknown> | null;
};

const ProductList = ({ role: roleProp }: ProductListProps) => {
  const { companyId, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Ensure role is only "admin" or "seller" (not "buyer")
  const role: "admin" | "seller" =
    roleProp || (userRole === "seller" ? "seller" : "admin");
  const isAdmin = role === "admin";

  const [activeTab, setActiveTab] = useState("all");
  const [approvalStatusFilter, setApprovalStatusFilter] = useState<
    "all" | "pending" | "approved" | "rejected"
  >("all");
  const [updatedStatusFilter, setUpdatedStatusFilter] =
    useState<UpdatedStatusFilter>("all");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<AdminProduct | null>(
    null
  );
  const [toast, setToast] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const [activeStatusOverrides, setActiveStatusOverrides] = useState<
    Record<string, boolean>
  >({});
  const [featuredProducts, setFeaturedProducts] = useState<
    FeaturedProductEntry[]
  >([]);
  const [featuredToggleId, setFeaturedToggleId] = useState<string | null>(null);

  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewProductId, setReviewProductId] = useState<string | null>(null);
  const [reviewComparison, setReviewComparison] = useState<{
    current: Record<string, unknown>;
    proposed: Record<string, unknown>;
  } | null>(null);

  const [listingRejectProductId, setListingRejectProductId] = useState<
    string | null
  >(null);
  const [listingRejectReason, setListingRejectReason] = useState("");

  /** Inline decline step inside the review modal (no second dialog). */
  const [reviewDeclineExpanded, setReviewDeclineExpanded] = useState(false);
  const [reviewDeclineReason, setReviewDeclineReason] = useState("");
  const reviewDeclineSectionRef = useRef<HTMLDivElement>(null);
  const [historyProductId, setHistoryProductId] = useState<string | null>(null);
  const [historyRows, setHistoryRows] = useState<SellerUpdateHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyChangesOpenId, setHistoryChangesOpenId] = useState<
    string | null
  >(null);
  /** Live listing used as baseline — same compare as Review changes. */
  const [historyLiveRecord, setHistoryLiveRecord] = useState<Record<
    string,
    unknown
  > | null>(null);

  const showToast = useCallback(
    (type: "success" | "error" | "info", message: string) => {
      setToast({ type, message });
      setTimeout(() => setToast(null), 5000);
    },
    []
  );

  useEffect(() => {
    const state = location.state as { productListTab?: string } | undefined;
    if (state?.productListTab === "pending-updates") {
      setActiveTab("pending-updates");
      navigate(location.pathname, { replace: true, state: null });
    } else if (state?.productListTab === "updated-status") {
      setActiveTab("updated-status");
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (
      activeTab === "pending" ||
      activeTab === "approved" ||
      activeTab === "rejected"
    ) {
      setApprovalStatusFilter("all");
    }
  }, [activeTab]);

  const { products, loading, error, pagination, setPagination, refresh } =
    useProductList({
      role,
      activeTab,
      updatedStatusFilter,
      approvalStatusFilter,
      autoFetch: true,
    });

  const {
    loading: actionsLoading,
    approveProduct,
    rejectProduct,
    submitForApproval,
    deleteProduct,
    updateProduct,
    saveAsDraft,
    publishProduct,
    updateProductActiveStatus,
  } = useProductActions({
    role,
    onSuccess: () => {
      refresh();
      setToast(null);
    },
    onError: (message) => showToast("error", message),
  });

  const closeListingRejectDialog = useCallback(() => {
    setListingRejectProductId(null);
    setListingRejectReason("");
  }, []);

  const openListingRejectDialog = useCallback((productId: string) => {
    setListingRejectProductId(productId);
    setListingRejectReason("");
  }, []);

  const closeHistoryDialog = useCallback(() => {
    setHistoryProductId(null);
    setHistoryRows([]);
    setHistoryChangesOpenId(null);
    setHistoryLiveRecord(null);
  }, []);

  const openSellerUpdateHistory = useCallback(
    async (productId: string) => {
      setHistoryProductId(productId);
      setHistoryChangesOpenId(null);
      setHistoryLiveRecord(null);
      setHistoryLoading(true);
      setHistoryRows([]);
      try {
        const [histRes, prodRes] = await Promise.all([
          isAdmin
            ? adminService.getProductSellerUpdateHistory(productId)
            : sellerService.getProductSellerUpdateHistory(productId),
          isAdmin
            ? adminService.getProductById(productId)
            : sellerService.getProductById(productId),
        ]);

        let live: Record<string, unknown> | null = null;
        if (
          prodRes.success &&
          prodRes.data &&
          typeof prodRes.data === "object" &&
          !Array.isArray(prodRes.data)
        ) {
          live = JSON.parse(JSON.stringify(prodRes.data)) as Record<
            string,
            unknown
          >;
        } else {
          const fromList = products.find((p) => p.id === productId);
          if (fromList) {
            live = JSON.parse(JSON.stringify(fromList)) as Record<
              string,
              unknown
            >;
          }
        }

        if (
          histRes.success &&
          histRes.data &&
          typeof histRes.data === "object" &&
          Array.isArray((histRes.data as { requests?: unknown }).requests)
        ) {
          setHistoryRows(
            (histRes.data as { requests: SellerUpdateHistoryRow[] }).requests
          );
          if (live) {
            setHistoryLiveRecord(live);
          } else {
            showToast(
              "error",
              "History loaded but the listing could not be loaded for comparison."
            );
          }
        } else {
          showToast("error", histRes.message || "Failed to load history");
          closeHistoryDialog();
        }
      } catch {
        showToast("error", "Failed to load history");
        closeHistoryDialog();
      } finally {
        setHistoryLoading(false);
      }
    },
    [isAdmin, showToast, closeHistoryDialog, products]
  );

  const handleApproveProduct = async (productId: string) => {
    const success = await approveProduct(productId);
    if (success) {
      showToast("success", "Product approved successfully");
    } else {
      showToast("error", "Failed to approve product");
    }
  };

  const confirmListingReject = useCallback(async () => {
    if (!listingRejectProductId) return;
    const trimmed = listingRejectReason.trim();
    if (!trimmed) {
      showToast(
        "error",
        "Please enter a rejection reason. Sellers will see this on their product list."
      );
      return;
    }
    const success = await rejectProduct(listingRejectProductId, trimmed);
    if (success) {
      showToast("success", "Product rejected successfully");
      closeListingRejectDialog();
    }
  }, [
    listingRejectProductId,
    listingRejectReason,
    rejectProduct,
    showToast,
    closeListingRejectDialog,
  ]);

  const closeReviewDialog = useCallback(() => {
    setReviewDialogOpen(false);
    setReviewProductId(null);
    setReviewComparison(null);
    setReviewDeclineExpanded(false);
    setReviewDeclineReason("");
  }, []);

  const openPendingUpdateReview = useCallback(
    async (productId: string, opts?: { expandDecline?: boolean }) => {
      if (!isAdmin) return;
      setReviewProductId(productId);
      setReviewDialogOpen(true);
      setReviewLoading(true);
      setReviewComparison(null);
      setReviewDeclineExpanded(false);
      setReviewDeclineReason("");
      try {
        const res = await adminService.getProductPendingReview(productId);
        if (res.success && res.data?.current && res.data?.proposed) {
          setReviewComparison({
            current: res.data.current as Record<string, unknown>,
            proposed: res.data.proposed as Record<string, unknown>,
          });
          if (opts?.expandDecline) {
            setReviewDeclineExpanded(true);
          }
        } else {
          showToast("error", res.message || "Failed to load comparison");
          closeReviewDialog();
        }
      } catch {
        showToast("error", "Failed to load comparison");
        closeReviewDialog();
      } finally {
        setReviewLoading(false);
      }
    },
    [isAdmin, showToast, closeReviewDialog]
  );

  useEffect(() => {
    if (
      reviewDeclineExpanded &&
      reviewDeclineSectionRef.current &&
      !reviewLoading
    ) {
      reviewDeclineSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [reviewDeclineExpanded, reviewLoading]);

  const handleApprovePendingUpdate = async (productId: string) => {
    const success = await approveProduct(productId);
    if (success) {
      closeReviewDialog();
      showToast(
        "success",
        "Seller update approved and applied to the live listing"
      );
    } else {
      showToast("error", "Failed to approve update");
    }
  };

  const confirmReviewDecline = useCallback(async () => {
    if (!reviewProductId) return;
    const trimmed = reviewDeclineReason.trim();
    if (!trimmed) {
      showToast(
        "error",
        "Please enter a reason. The seller will see it for this declined update."
      );
      return;
    }
    const success = await rejectProduct(reviewProductId, trimmed);
    if (success) {
      showToast("success", "Seller update declined; live listing unchanged");
      closeReviewDialog();
    }
  }, [reviewProductId, reviewDeclineReason, rejectProduct, showToast, closeReviewDialog]);

  const handleSubmitForApproval = async (productId: string) => {
    const success = await submitForApproval(productId);
    if (success) {
      showToast("success", "Product submitted for approval successfully");
    } else {
      showToast("error", "Failed to submit product");
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    const success = await deleteProduct(productId);
    if (success) {
      showToast("success", "Product deleted successfully");
    } else {
      showToast("error", "Failed to delete product");
    }
  };

  const handleDuplicateProduct = async (productId: string) => {
    try {
      const response = isAdmin
        ? await adminService.duplicateProduct(productId)
        : await sellerService.duplicateProduct(productId);

      if (response?.success) {
        refresh();
        showToast("success", "Product duplicated successfully");
      } else {
        showToast("error", response?.message || "Failed to duplicate product");
      }
    } catch {
      showToast("error", "Failed to duplicate product");
    }
  };

  const handleMarkAsActive = async (productId: string) => {
    const success = await updateProductActiveStatus(productId, true);
    if (success) {
      setActiveStatusOverrides((prev) => ({ ...prev, [productId]: true }));
      showToast("success", "Product marked as active");
      refresh();
    } else {
      showToast("error", "Failed to mark product as active");
    }
  };

  const handleMarkAsInactive = async (productId: string) => {
    const success = await updateProductActiveStatus(productId, false);
    if (success) {
      setActiveStatusOverrides((prev) => ({ ...prev, [productId]: false }));
      showToast("success", "Product marked as inactive");
      refresh();
    } else {
      showToast("error", "Failed to mark product as inactive");
    }
  };

  const featuredProductIds = useMemo(
    () => new Set(getActiveFeaturedProductIds(featuredProducts)),
    [featuredProducts],
  );

  const loadFeaturedProducts = useCallback(async () => {
    if (isAdmin) return;
    try {
      const response = await sellerService.getStorefrontSettings(true);
      if (response.success && response.data) {
        setFeaturedProducts(parseFeaturedProductsFromSettings(response.data));
      }
    } catch {
      // Non-blocking: seller can still toggle; errors surface on save.
    }
  }, [isAdmin]);

  useEffect(() => {
    void loadFeaturedProducts();
  }, [loadFeaturedProducts]);

  const handleToggleFeatured = useCallback(
    async (productId: string) => {
      if (isAdmin) return;

      const isFeatured = featuredProductIds.has(productId);
      const product = products.find((item) => item.id === productId);

      if (!isFeatured && product?.approvalStatus !== "approved") {
        showToast(
          "info",
          "Only approved products can be featured on your shop page.",
        );
        return;
      }

      let nextFeatured: FeaturedProductEntry[];
      try {
        nextFeatured = toggleFeaturedProduct(
          featuredProducts,
          productId,
          !isFeatured,
        );
      } catch (error) {
        showToast(
          "error",
          error instanceof Error
            ? error.message
            : "Failed to update featured products",
        );
        return;
      }

      setFeaturedToggleId(productId);
      try {
        const response = await sellerService.updateStorefrontSettings({
          featuredProducts: toStorefrontFeaturedPayload(nextFeatured),
        });
        if (response.success) {
          setFeaturedProducts(nextFeatured);
          showToast(
            "success",
            isFeatured
              ? "Product removed from featured"
              : "Product marked as featured",
          );
        } else {
          showToast(
            "error",
            response.message || "Failed to update featured products",
          );
        }
      } catch {
        showToast("error", "Failed to update featured products");
      } finally {
        setFeaturedToggleId(null);
      }
    },
    [
      isAdmin,
      featuredProductIds,
      featuredProducts,
      products,
      showToast,
    ],
  );

  const pendingReviewDiffRows = useMemo(() => {
    if (!reviewComparison) return [];
    return buildPendingReviewDiffRows(
      reviewComparison.current,
      reviewComparison.proposed
    );
  }, [reviewComparison]);

  const displayProducts = useMemo((): AdminProduct[] => {
    return products.map((product) => {
      if (
        Object.prototype.hasOwnProperty.call(activeStatusOverrides, product.id)
      ) {
        return {
          ...product,
          isEnabled: activeStatusOverrides[product.id],
          activeStatus: activeStatusOverrides[product.id]
            ? "active"
            : "inactive",
        };
      }
      return product;
    });
  }, [products, activeStatusOverrides]);

  const handleViewDetails = (productId: string) => {
    navigate(getProductDetailPath(productId));
  };

  const handleEditProduct = (productId: string) => {
    if (isAdmin) {
      navigate(`/admin/create_product/${productId}`);
    } else {
      navigate(`/seller/create_product/${productId}`);
    }
  };

  const handleUpdateProduct = async (productData: any) => {
    if (!editingProduct) return;
    const success = await updateProduct(editingProduct.id, productData);
    if (success) {
      setIsEditDialogOpen(false);
      setEditingProduct(null);
      showToast("success", "Product updated successfully!");
    } else {
      showToast("error", "Failed to update product");
    }
  };

  const handleSaveAsDraft = async (productData: any) => {
    if (!editingProduct) return;
    const success = await saveAsDraft(editingProduct.id, productData);
    if (success) {
      setIsEditDialogOpen(false);
      setEditingProduct(null);
      showToast("success", "Product saved as draft!");
    } else {
      showToast("error", "Failed to save as draft");
    }
  };

  const handlePublishProduct = async (productData: any) => {
    if (!editingProduct) return;
    if (isAdmin) {
      const success = await publishProduct(editingProduct.id, productData);
      if (success) {
        setIsEditDialogOpen(false);
        setEditingProduct(null);
        showToast("success", "Product published successfully!");
      } else {
        showToast("error", "Failed to publish product");
      }
    } else {
      // Seller edit flow must submit current form payload so pending update snapshot
      // includes changed fields (e.g. tieredPricing), not just trigger approval status.
      const success = await updateProduct(editingProduct.id, productData);
      if (success) {
        setIsEditDialogOpen(false);
        setEditingProduct(null);
        showToast(
          "success",
          "Product update submitted for review successfully"
        );
      } else {
        showToast("error", "Failed to submit product update");
      }
    }
  };

  // Create column definitions with memoization
  const columns = useMemo(
    () =>
      createProductColumns({
        isAdmin,
        stickySellerActions: false,
        loading,
        actionsLoading,
        onViewDetails: handleViewDetails,
        onEditProduct: handleEditProduct,
        onApproveProduct: handleApproveProduct,
        onRejectProduct: openListingRejectDialog,
        onSubmitForApproval: handleSubmitForApproval,
        onDeleteProduct: handleDeleteProduct,
        onDuplicateProduct: handleDuplicateProduct,
        onMarkAsActive: handleMarkAsActive,
        onMarkAsInactive: handleMarkAsInactive,
        ...(isAdmin
          ? {
              onReviewPendingUpdate: openPendingUpdateReview,
              onApprovePendingUpdate: handleApprovePendingUpdate,
              onRejectPendingUpdate: (productId: string) =>
                void openPendingUpdateReview(productId, { expandDecline: true }),
            }
          : {}),
        onViewSellerUpdateHistory: openSellerUpdateHistory,
        ...(!isAdmin
          ? {
              featuredProductIds,
              onToggleFeatured: handleToggleFeatured,
              featuredToggleId,
            }
          : {}),
        statusStyles: AdminproductTableConfig.styles?.statusStyles,
      }),
    [
      isAdmin,
      loading,
      actionsLoading,
      handleViewDetails,
      handleEditProduct,
      handleApproveProduct,
      openListingRejectDialog,
      handleSubmitForApproval,
      handleDeleteProduct,
      handleDuplicateProduct,
      handleMarkAsActive,
      handleMarkAsInactive,
      openPendingUpdateReview,
      handleApprovePendingUpdate,
      openSellerUpdateHistory,
      featuredProductIds,
      handleToggleFeatured,
      featuredToggleId,
      activeStatusOverrides,
    ]
  );

  return (
    <div className="space-y-8 mt-5">
      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-[9999] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : toast.type === "error"
                ? "bg-red-500 text-white"
                : "bg-blue-500 text-white"
          }`}
        >
          <div className="flex items-center gap-2">
            {toast.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : toast.type === "error" ? (
              <XCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <Info className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="flex-1 font-medium text-sm">{toast.message}</p>
            <button
              onClick={() => setToast(null)}
              className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
              aria-label="Close notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {isAdmin && (
        <>
          <RejectProductReasonDialog
            open={listingRejectProductId !== null}
            title="Reject product"
            description="Sellers will see this reason on their product list so they know what to fix before resubmitting."
            reason={listingRejectReason}
            onReasonChange={setListingRejectReason}
            onClose={closeListingRejectDialog}
            onConfirm={() => void confirmListingReject()}
            loading={actionsLoading}
          />
        </>
      )}

      <Dialog
        open={historyProductId !== null}
        onOpenChange={(open) => !open && !historyLoading && closeHistoryDialog()}
      >
        <DialogContent
          className="sm:max-w-4xl bg-white max-h-[min(90vh,640px)] flex flex-col"
          showCloseButton={!historyLoading}
        >
          <DialogHeader>
            <DialogTitle>Listing update history</DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto border rounded-md">
            {historyLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
                <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                Loading…
              </div>
            ) : historyRows.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground text-center">
                No history entries yet.
              </p>
            ) : (
              <ul className="divide-y text-sm">
                {historyRows.map((row) => {
                  const changesOpen = historyChangesOpenId === row.id;
                  const hasSnapshot =
                    row.submittedSnapshot != null &&
                    typeof row.submittedSnapshot === "object" &&
                    !Array.isArray(row.submittedSnapshot) &&
                    Object.keys(row.submittedSnapshot as object).length > 0;
                  const patchRows = buildSubmittedSnapshotDisplayRows(
                    row.submittedSnapshot
                  );
                  const diffRows =
                    historyLiveRecord != null
                      ? buildSellerUpdateHistoryDiffRows(
                          historyLiveRecord,
                          row.submittedSnapshot
                        )
                      : [];
                  const hasDiffVsLive = diffRows.length > 0;
                  return (
                    <li key={row.id} className="p-3 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            row.status === "pending"
                              ? "bg-amber-100 text-amber-900"
                              : row.status === "approved"
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-red-100 text-red-800"
                          }`}
                        >
                          {row.status}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Submitted{" "}
                          {new Date(row.submittedAt).toLocaleString()}
                        </span>
                      </div>
                      {row.resolvedAt ? (
                        <p className="text-xs text-muted-foreground">
                          Resolved {new Date(row.resolvedAt).toLocaleString()}
                        </p>
                      ) : null}
                      {row.rejectionReason ? (
                        <p className="text-xs text-foreground whitespace-pre-wrap border-l-2 border-red-200 pl-2">
                          {row.rejectionReason}
                        </p>
                      ) : null}
                      {!hasSnapshot || patchRows.length === 0 ? (
                        <p className="text-[11px] text-muted-foreground leading-snug">
                          No field-level snapshot for this entry (older data may
                          not include details).
                        </p>
                      ) : (
                        <div>
                          <button
                            type="button"
                            className="text-xs font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-sm"
                            onClick={() =>
                              setHistoryChangesOpenId((id) =>
                                id === row.id ? null : row.id
                              )
                            }
                            aria-expanded={changesOpen}
                          >
                            {changesOpen
                              ? "Hide details"
                              : "View comparision"}
                          </button>
                          {changesOpen ? (
                            <div className="mt-3 space-y-4">
                              {hasDiffVsLive ? (
                                <div className="rounded-md border border-border bg-muted/10 overflow-hidden">
                                  <div className="px-3 py-2 border-b bg-muted/40">
                                    <p className="text-xs font-semibold text-foreground">
                                      Compared to current live listing
                                    </p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5">
                                      Only fields that still differ from live (same
                                      as Review changes).
                                    </p>
                                  </div>
                                  <div className="overflow-x-auto max-h-[min(45vh,320px)] overflow-y-auto">
                                    <table className="w-full text-xs">
                                      <thead className="sticky top-0 z-[1] bg-muted/90 backdrop-blur-sm border-b">
                                        <tr className="text-left">
                                          <th className="p-2 font-semibold w-[22%] align-top">
                                            Field
                                          </th>
                                          <th className="p-2 font-semibold w-[39%] align-top">
                                            Current (live)
                                          </th>
                                          <th className="p-2 font-semibold w-[39%] align-top">
                                            In this submission
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {diffRows.map((r) => (
                                          <tr
                                            key={r.key}
                                            className="border-b border-border/60 align-top"
                                          >
                                            <td className="p-2 font-mono text-[11px] text-muted-foreground">
                                              {r.key}
                                            </td>
                                            <td className="p-2 min-w-0 bg-red-50/50">
                                              {r.currentCell}
                                            </td>
                                            <td className="p-2 min-w-0 bg-green-50/50">
                                              {r.proposedCell}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : null}
                              {!hasDiffVsLive ? (
                                <p className="text-xs text-muted-foreground">
                                  No differences against current live values for this
                                  entry.
                                </p>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="primary" onClick={closeHistoryDialog}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">
            {isAdmin ? "Product Listings" : "My Product Listings"}
          </h1>
          <p className="text-muted-foreground">
            {isAdmin
              ? "Manage products created by admin for sellers or submitted by sellers. Use the Pending updates tab for seller edits to approved listings."
              : "Manage your products and track their approval status"}
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => refresh()}
            disabled={loading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button
            variant="primary"
            disabled={actionsLoading || !companyId}
            onClick={() => {
              const route = isAdmin
                ? "/admin/create_product"
                : "/seller/create_product";
              navigate(route);
            }}
          >
            {!companyId
              ? "Loading..."
              : isAdmin
                ? "Create Product for Seller"
                : "Create Product"}
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Products Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>
                {isAdmin ? "Product Listings" : "My Product Listings"}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {isAdmin
                  ? "Manage products created by admin for sellers or submitted by sellers"
                  : "Manage your products and track their approval status"}
              </p>
            </div>
            {/* <div className="flex w-full flex-col gap-1.5 sm:w-56 shrink-0">
              <span className="text-xs font-medium text-muted-foreground">
                Approval status
              </span>
              <Select
                value={approvalStatusFilter}
                onValueChange={(v) =>
                  setApprovalStatusFilter(
                    v as "all" | "pending" | "approved" | "rejected"
                  )
                }
                disabled={
                  activeTab === "pending" ||
                  activeTab === "approved" ||
                  activeTab === "rejected"
                }
              >
                <SelectTrigger className="w-full bg-white">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div> */}
          </div>
        </CardHeader>
        <CardContent>
          <Tabs
            defaultValue="all"
            value={activeTab}
            onValueChange={setActiveTab}
          >
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-1 bg-gray-100 rounded-lg p-1 h-auto min-h-10">
              <TabsTrigger
                value="all"
                className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
              >
                All Products
              </TabsTrigger>
              {isAdmin ? (
                <>
                  <TabsTrigger
                    value="admin-created"
                    className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
                  >
                    Admin Created for Sellers
                  </TabsTrigger>
                  <TabsTrigger
                    value="seller-submitted"
                    className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
                  >
                    Seller Submitted
                  </TabsTrigger>
                  <TabsTrigger
                    value="pending"
                    className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
                  >
                    Pending Review
                  </TabsTrigger>
                  <TabsTrigger
                    value="pending-updates"
                    className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
                  >
                    Update Pending
                  </TabsTrigger>
                  <TabsTrigger
                    value="updated-status"
                    className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
                  >
                    Update status
                  </TabsTrigger>
                </>
              ) : (
                <>
                  <TabsTrigger
                    value="draft"
                    className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
                  >
                    Drafts
                  </TabsTrigger>
                  <TabsTrigger
                    value="pending"
                    className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
                  >
                    Pending Approval
                  </TabsTrigger>
                  <TabsTrigger
                    value="approved"
                    className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
                  >
                    Approved
                  </TabsTrigger>
                  <TabsTrigger
                    value="rejected"
                    className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
                  >
                    Rejected
                  </TabsTrigger>
                  <TabsTrigger
                    value="updated-status"
                    className="data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-md"
                  >
                    Update status
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value={activeTab} className="mt-6">
              {activeTab === "updated-status" && (
                <div className="mb-6 flex flex-col gap-4 rounded-lg border border-border bg-card p-4 shadow-sm sm:flex-row sm:items-end sm:justify-between sm:gap-6">
                  <div className="space-y-1 min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      Seller listing updates
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      These are <strong>approved</strong> products where you or
                      the seller submitted changes that required admin review.
                      Filter by whether that update is still waiting, was
                      applied to the live listing, or was declined.
                    </p>
                  </div>
                  <div className="flex w-full flex-col gap-1.5 sm:w-56 shrink-0">
                    <span className="text-xs font-medium text-muted-foreground">
                      Update review status
                    </span>
                    <Select
                      value={updatedStatusFilter}
                      onValueChange={(v) =>
                        setUpdatedStatusFilter(v as UpdatedStatusFilter)
                      }
                    >
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem
                          value="all"
                          className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                        >
                          All updates
                        </SelectItem>
                        <SelectItem
                          value="pending"
                          className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                        >
                          Pending review
                        </SelectItem>
                        <SelectItem
                          value="approved"
                          className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                        >
                          Update approved (applied)
                        </SelectItem>
                        <SelectItem
                          value="rejected"
                          className="border border-transparent transition-colors hover:rounded-md data-[highlighted]:rounded-md hover:border-blue-700 hover:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:!border-blue-700 data-[highlighted]:shadow-[inset_0_0_0_1px_rgb(29,78,216)] data-[highlighted]:bg-white data-[highlighted]:text-foreground"
                        >
                          Update declined
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                  <span>Loading products...</span>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    {activeTab === "updated-status"
                      ? "No products match this update review filter, or no seller listing updates yet."
                      : "No products available in this category."}
                  </p>
                </div>
              ) : (
                <div className="w-full min-w-0">
                  <TanStackDataTable
                    data={displayProducts}
                    columns={columns}
                    isLoading={loading}
                    serverPagination={
                      pagination.totalPages > 1 &&
                      pagination.total > pagination.limit
                        ? {
                            pageIndex: pagination.page,
                            pageSize: pagination.limit,
                            total: pagination.total,
                            totalPages: pagination.totalPages,
                            onPageChange: (page: number) => {
                              setPagination((prev) => ({ ...prev, page }));
                            },
                          }
                        : undefined
                    }
                    emptyMessage="No products available in this category."
                    styles={{
                      headerBg: AdminproductTableConfig.styles?.headerBg,
                      headerText: AdminproductTableConfig.styles?.headerText,
                      rowHover: AdminproductTableConfig.styles?.rowHover,
                    }}
                    config={{
                      enableSorting: true,
                      enablePagination: false, // Server-side pagination
                      enableZebraStriping: false,
                      // Seller and admin: allow horizontal scroll on narrow viewports (wide tables).
                      disableHorizontalScroll: false,
                    }}
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {reviewDialogOpen && isAdmin && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
          onClick={() =>
            !reviewLoading && !actionsLoading && closeReviewDialog()
          }
          role="presentation"
        >
          <div
            className={`bg-white rounded-lg shadow-xl max-w-5xl w-full overflow-hidden flex flex-col transition-[max-height] duration-200 ease-out ${
              reviewDeclineExpanded ? "max-h-[min(92vh,880px)]" : "max-h-[90vh]"
            }`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-pending-review-title"
          >
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h2
                id="product-pending-review-title"
                className="text-lg font-semibold"
              >
                {reviewDeclineExpanded
                  ? "Decline seller update"
                  : "Review seller update"}
              </h2>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={closeReviewDialog}
                disabled={reviewLoading || actionsLoading}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="overflow-y-auto flex-1 min-h-0 p-4">
              {reviewLoading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                  Loading comparison…
                </div>
              ) : reviewComparison ? (
                <>
                  {!reviewDeclineExpanded ? (
                    <p className="text-sm text-muted-foreground mb-4">
                      <strong>Current (live)</strong> vs{" "}
                      <strong>proposed</strong> after approval. Only differing
                      fields are shown.
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground mb-4">
                      You are declining the proposed changes below. The{" "}
                      <strong>live</strong> listing stays as-is until the seller
                      resubmits.
                    </p>
                  )}
                  <div className="overflow-x-auto border rounded-md">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/50 text-left border-b">
                          <th className="p-2 font-medium w-[22%]">Field</th>
                          <th className="p-2 font-medium w-[39%]">
                            Current (live)
                          </th>
                          <th className="p-2 font-medium w-[39%]">Proposed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingReviewDiffRows.map((row) => (
                          <tr key={row.key} className="border-b align-top">
                            <td className="p-2 font-mono text-xs">{row.key}</td>
                            <td className="p-2 bg-red-50/60 break-words align-top">
                              {row.currentCell}
                            </td>
                            <td className="p-2 bg-green-50/60 break-words align-top">
                              {row.proposedCell}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {pendingReviewDiffRows.length === 0 && (
                    <p className="text-sm text-muted-foreground mt-2">
                      No scalar differences detected in the comparison payload.
                    </p>
                  )}
                  {reviewDeclineExpanded && (
                    <div
                      ref={reviewDeclineSectionRef}
                      className="mt-5 rounded-lg border border-destructive/25 bg-destructive/[0.06] p-4 space-y-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Feedback for the seller
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          They will see this on their product list and in update
                          history. Be specific so they know what to change.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="review-inline-decline-reason">
                          Reason for declining
                        </Label>
                        <Textarea
                          id="review-inline-decline-reason"
                          value={reviewDeclineReason}
                          onChange={(e) => setReviewDeclineReason(e.target.value)}
                          placeholder="Explain what needs to change before they resubmit…"
                          rows={4}
                          disabled={actionsLoading}
                          className="resize-y min-h-[100px] bg-white"
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t px-4 py-3 bg-muted/30 shrink-0">
              {reviewDeclineExpanded ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setReviewDeclineExpanded(false);
                      setReviewDeclineReason("");
                    }}
                    disabled={reviewLoading || actionsLoading}
                  >
                    Back to comparison
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={reviewLoading || actionsLoading}
                    onClick={() => void confirmReviewDecline()}
                  >
                    {actionsLoading ? "Declining…" : "Confirm decline"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeReviewDialog}
                    disabled={reviewLoading || actionsLoading}
                  >
                    Close
                  </Button>
                  {reviewProductId && (
                    <>
                      <Button
                        type="button"
                        variant="destructive"
                        disabled={reviewLoading || actionsLoading}
                        onClick={() => {
                          setReviewDeclineExpanded(true);
                          setReviewDeclineReason("");
                        }}
                      >
                        Reject update
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        disabled={reviewLoading || actionsLoading}
                        onClick={() =>
                          handleApprovePendingUpdate(reviewProductId)
                        }
                      >
                        Approve update
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Dialog */}
      {editingProduct && (
        <StoreProductDialog
          trigger={<div />}
          product={editingProduct}
          isEdit={true}
          isAdminMode={isAdmin}
          companyId={companyId}
          isLoading={actionsLoading}
          onUpdate={handleUpdateProduct}
          onSaveDraft={handleSaveAsDraft}
          onPublish={handlePublishProduct}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
        />
      )}
    </div>
  );
};

export default ProductList;
